import { memo, useEffect, useRef, useState } from "react";
import { Shimmer } from "@repo/ui";
import "./mathJaxGlobals";
import { ensureMathJaxLoaded } from "./ensureMathJaxLoaded";
import {
  ensureSpaceAfterMathML,
  stripInlineBackgroundStyles,
  unwrapStrongAndBold,
} from "./stripInlineBackgroundStyles";
import styles from "./MathJaxHtml.module.scss";

export type MathJaxHtmlProps = {
  questionNo?: number | null;
  content?: string | null;
  className?: string;
  disableShimmer?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
};

function buildQuestionNoPrefix(questionNo: number | null | undefined): string {
  if (!questionNo || questionNo === 0) return "";
  return `<span class="${styles.questionNo}">Q${questionNo}.&nbsp;</span>`;
}

function insertQuestionNoAfterFirstBlockOpen(
  html: string,
  prefix: string,
): string {
  if (!prefix) return html;
  return html.replace(/^\s*<p(\s[^>]*)?>/i, (openTag) => `${openTag}${prefix}`);
}

export const MathJaxHtml = memo(function MathJaxHtml({
  questionNo = null,
  content,
  className,
  disableShimmer = false,
  onLoadingChange,
}: MathJaxHtmlProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTypesetting, setIsTypesetting] = useState(false);
  const normalizedContent =
    content === null || content === undefined ? "" : String(content);
  const html =
    normalizedContent.trim() &&
    normalizedContent !== "undefined" &&
    normalizedContent !== "null"
      ? normalizedContent
      : "";

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (!html) {
      el.innerHTML = "";
      setIsTypesetting(false);
      onLoadingChange?.(false);
      return;
    }

    let cancelled = false;
    setIsTypesetting(true);
    onLoadingChange?.(true);

    const run = async () => {
      try {
        await ensureMathJaxLoaded();
        if (cancelled || !containerRef.current) return;
        const node = containerRef.current;
        const mj = window.MathJax;
        if (!mj?.typesetPromise) return;
        mj.typesetClear?.([node]);
        const bodyHtml = ensureSpaceAfterMathML(
          unwrapStrongAndBold(stripInlineBackgroundStyles(html)),
        );
        const prefix = buildQuestionNoPrefix(questionNo);
        const bodyWithQ = insertQuestionNoAfterFirstBlockOpen(bodyHtml, prefix);
        const bodyInner =
          prefix && bodyWithQ === bodyHtml ? `${prefix}${bodyHtml}` : bodyWithQ;
        node.innerHTML = `<div class="mathjax-html-body">${bodyInner}</div>`;
        await mj.typesetPromise([node]);
      } catch {
        // intentionally ignored
      } finally {
        if (!cancelled) {
          setIsTypesetting(false);
          onLoadingChange?.(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [html, onLoadingChange, questionNo]);

  const showShimmer = !disableShimmer && isTypesetting && Boolean(html);

  return (
    <div className={`${styles.root} ${className ?? ""}`.trim()}>
      {showShimmer ? (
        <div className={styles.shimmerOverlay} aria-hidden>
          <Shimmer width="100%" height={72} borderRadius={8} />
        </div>
      ) : null}
      <div ref={containerRef} className={styles.host} />
    </div>
  );
});
